import {useLayoutEffect, useState} from "react";
import {Button, Dialog, DialogContent, DialogTitle, Divider, Grid, Paper, Stack, Typography} from "@mui/material";
import {CheckCircle} from "@mui/icons-material";
import {Link} from "react-router";
import {StyledButtonContained, StyledButtonOutlined} from "../../../utils/uiUtils";
import {UserBadge} from "../../../component/user/UserBadge";
import {CreateEditDialog} from "../../../component/CreateEditDialog/CreateEditDialog";
import {addUser, deleteCustomerUser, fetchCustomer, fetchCustomerUsers, fetchJob, patchJob} from "../../../utils/apiCalls";

export const AdminAccountCreationSuccess = ({open, onClose}) => {
    const [clientDetails, setClientDetails] = useState([]);
    const [orderNumber, setOrderNumber] = useState("");
    const [credentialsSentOpen, setCredentialsSentOpen] = useState(false);
    const [addUsers, setAddUsers] = useState(false);
    const [addMemberFormOpen, setAddMemberFormOpen] = useState(false);
    const [formData, setFormData] = useState([]);
    const [customer, setCustomer] = useState("");

    const _orderUUID = window?.location?.href?.split("uuid=")[1];

    // Loads order + customer data into state
    const loadOrderData = () => {
        fetchJob(_orderUUID)
            .then(order => {
                setOrderNumber(order.order_number);
                setCustomer(order.customer);
                return fetchCustomer(order.customer);
            })
            .then(customerData => setClientDetails(customerData))
            .catch(err => console.log(err));
    };

    const updateOrderStatus = () => {
        fetchJob(_orderUUID)
            .then(order => {
                if (order.status === 'draft') {
                    patchJob(_orderUUID, order.customer, {status: "preparing_for_shipment"})
                        .catch(err => console.log(err));
                }
            })
            .catch(err => console.log(err));
    };

    const handleClose = () => {
        updateOrderStatus();
        loadOrderData();
        fetchCustomerUsers(customer)
            .then(users => setFormData(users.results))
            .catch(err => console.log(err));
        setCredentialsSentOpen(true);
    };

    const handleAddUsers = () => {
        setAddUsers(true);
        setCredentialsSentOpen(false);
    };

    const openAddMemberForm = () => {
        setAddMemberFormOpen(true);
        setAddUsers(false);
    };

    const handleAddCustomerUser = (data) => {
        addUser(customer, data, false)
            .then(() => {
                fetchCustomerUsers(customer)
                    .then(users => {
                        setFormData(users.results);
                        setAddMemberFormOpen(false);
                        setAddUsers(true);
                    })
                    .catch(e => console.log(e));
            })
            .catch(e => console.log(e));
    };

    const onDelete = (customerId, uuid) => {
        deleteCustomerUser(customerId, uuid)
            .then(() => fetchCustomerUsers(customerId)
                .then(users => setFormData(users.results)))
            .catch(e => console.log(e));
    };

    useLayoutEffect(() => {
        loadOrderData();
    }, [open]);

    return (
        <>
            {/* Account Created dialog */}
            <Dialog
                open={open}
                fullWidth
                onClose={onClose}
                PaperProps={{sx: {borderRadius: "8px", backgroundColor: "#3A3E3F", color: "#FFFFFF", p: 2}}}
            >
                <DialogTitle component="div" color={"primary"} display={"flex"} flexDirection={"column"} alignItems={"center"}>
                    <CheckCircle sx={{color: "#FFFFFF", height: "12rem", width: "12rem"}}/>
                    <Typography variant="h4" sx={{color: "#FFFFFF"}}>Account Created</Typography>
                </DialogTitle>
                <Divider sx={{backgroundColor: "#5F5F5F", width: "100%"}}/>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} textAlign={"center"}>
                            <Typography fontSize={"16px"} variant="h6" fontWeight={400} sx={{color: "#FFFFFF"}}>
                                An account and job have been successfully created for:
                            </Typography>
                            <Typography align="center" fontSize={"18px"} fontWeight={700} sx={{color: "#FFFFFF"}}>
                                {clientDetails?.company_name}
                            </Typography>
                            <Typography align="center" fontSize={"18px"} sx={{color: "#FFFFFF"}}>
                                With the Job ID <b>{orderNumber}</b>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper sx={{background: "transparent", border: "1px solid #FFFFFF", borderRadius: "8px", height: "8rem", color: "#FFFFFF", px: 4, py: 2, display: "flex", flexDirection: "column", justifyContent: "space-between"}}>
                                <Stack direction={"column"}>
                                    <Typography fontSize={"16px"} variant="h6" fontWeight={400}>Username:</Typography>
                                    <Typography fontSize={"16px"} variant="h6" fontWeight={400}>{clientDetails?.email}</Typography>
                                </Stack>
                                <Stack direction={"column"}>
                                    <Typography fontSize={"16px"} variant="h6" fontWeight={400}>Password:</Typography>
                                    <Typography fontSize={"16px"} variant="h6" fontWeight={400}>************</Typography>
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <StyledButtonContained
                                variant="contained"
                                fullWidth={true}
                                sx={{textTransform: "none", height: "3rem", fontFamily: "Poppins"}}
                                onClick={handleClose}
                            >
                                Send Credentials & Confirm Job
                            </StyledButtonContained>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            {/* Success dialog */}
            <Dialog
                open={credentialsSentOpen}
                fullWidth
                onClose={() => setCredentialsSentOpen(false)}
                PaperProps={{sx: {borderRadius: "8px", backgroundColor: "#3A3E3F", color: "#FFFFFF", p: 2}}}
            >
                <DialogTitle component="div" color={"primary"} display={"flex"} flexDirection={"column"} alignItems={"center"}>
                    <CheckCircle sx={{color: "#FFFFFF", height: "12rem", width: "12rem"}}/>
                    <Typography variant="h4" sx={{color: "#FFFFFF"}}>Success</Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} justifyContent="center">
                        <Grid item xs={12} textAlign={"center"}>
                            <Typography fontSize={"16px"} variant="h6" fontWeight={400} sx={{color: "#FFFFFF"}}>
                                The Credentials have been successfully sent to the user's email address
                            </Typography>
                        </Grid>
                        <Grid item xs={12} textAlign={"center"}>
                            <Link to="/Jobs" style={{textDecoration: "none"}}>
                                <StyledButtonContained
                                    variant="contained"
                                    sx={{backgroundColor: "#FFC540", textTransform: "none", height: "3rem", borderRadius: "30px", fontFamily: "Poppins", color: "#3A3E3F"}}
                                >
                                    Continue
                                </StyledButtonContained>
                            </Link>
                        </Grid>
                        <Grid item xs={12} textAlign={"center"}>
                            <Button
                                variant="text"
                                sx={{textTransform: "none", height: "3rem", fontFamily: "Poppins", color: "#FFC540"}}
                                onClick={handleAddUsers}
                            >
                                Add users to the team
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            {/* Add team dialog */}
            <Dialog
                open={addUsers}
                fullWidth
                onClose={() => setAddUsers(false)}
                PaperProps={{sx: {borderRadius: "8px", backgroundColor: "#3A3E3F", color: "#FFFFFF", p: 2}}}
            >
                <DialogContent>
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <Typography variant="h4" sx={{color: "#FFFFFF"}} align="left">Add your team</Typography>
                        </Grid>
                        {formData.filter(member => member?.role).map(member => (
                            <Grid item key={member.uuid}>
                                <UserBadge
                                    value={{
                                        first_name:    member.first_name,
                                        last_name:     member.last_name,
                                        email:         member.email,
                                        phone_number:  member.phone_number,
                                        mobile_number: member.mobile_number,
                                        role:          member.role,
                                    }}
                                    canToggle={false}
                                    onDelete={member.role !== "admin" ? () => onDelete(member.customer, member.uuid) : undefined}
                                />
                            </Grid>
                        ))}
                        <Grid item>
                            <StyledButtonOutlined
                                variant="outlined"
                                sx={{height: "3rem", fontFamily: "Poppins", color: "#FFC540", border: "1px solid #FFC540", borderRadius: "20px"}}
                                onClick={openAddMemberForm}
                            >
                                Add
                            </StyledButtonOutlined>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" justifyContent="space-between">
                                <Grid item>
                                    <Button
                                        variant="text"
                                        onClick={() => setAddUsers(false)}
                                        sx={{textTransform: "none", height: "3rem", fontFamily: "Poppins", color: "#FFFFFF"}}
                                    >
                                        Skip
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Link to="/Jobs" style={{textDecoration: "none"}}>
                                        <StyledButtonContained variant="contained" onClick={() => setAddUsers(false)} sx={{height: "3rem"}}>
                                            Done
                                        </StyledButtonContained>
                                    </Link>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            {/* Add team member form */}
            <CreateEditDialog
                isDialogOpen={addMemberFormOpen}
                handleClose={() => {setAddMemberFormOpen(false); setAddUsers(true);}}
                variant="customerUser"
                title="Add Team Member"
                onSubmitFn={handleAddCustomerUser}
            />
        </>
    );
};
